import { useEffect, useMemo, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, SortAscending, ArrowClockwise, WarningCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import HeroCard from "@/components/HeroCard";
import ProjectCard from "@/components/ProjectCard";
import Logo from "@/components/Logo";
import { Skeleton } from "@/components/ui/skeleton";
import { Button } from "@/components/ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { toast } from "sonner";

const SORT_OPTIONS = [
  { key: "hardest", label: "Hardest first" },
  { key: "oldest", label: "Oldest first" },
  { key: "newest", label: "Newest first" },
  { key: "closest", label: "Closest to completion" },
  { key: "least", label: "Least complete" },
  { key: "time_remaining", label: "Estimated time remaining" },
];

const DIFFICULTY_RANK = { hard: 3, medium: 2, easy: 1 };

function estTimeRemaining(p) {
  const total = p.ai_meta?.estimated_minutes;
  if (typeof total !== "number") return null;
  const remainingFrac = 1 - (p.progress_pct || 0) / 100;
  return Math.max(0, total * remainingFrac);
}

function sortProjects(list, sortKey) {
  const arr = [...(list || [])];
  const byOldest = (a, b) => (a.created_at || "").localeCompare(b.created_at || "");
  const byNewest = (a, b) => (b.created_at || "").localeCompare(a.created_at || "");

  const cmp = {
    hardest: (a, b) => {
      const ra = DIFFICULTY_RANK[a.ai_meta?.difficulty] || 0;
      const rb = DIFFICULTY_RANK[b.ai_meta?.difficulty] || 0;
      // When both missing difficulty (rank 0) fall back to oldest first.
      if (rb !== ra) return rb - ra;
      return byOldest(a, b);
    },
    oldest: byOldest,
    newest: byNewest,
    closest: (a, b) => (b.progress_pct || 0) - (a.progress_pct || 0) || byOldest(a, b),
    least: (a, b) => (a.progress_pct || 0) - (b.progress_pct || 0) || byOldest(a, b),
    time_remaining: (a, b) => {
      const ta = estTimeRemaining(a);
      const tb = estTimeRemaining(b);
      // Items with no estimate go to the bottom; among them, oldest first.
      if (ta == null && tb == null) return byOldest(a, b);
      if (ta == null) return 1;
      if (tb == null) return -1;
      return ta - tb || byOldest(a, b);
    },
  };
  arr.sort(cmp[sortKey] || cmp.hardest);
  return arr;
}

const SORT_STORAGE_KEY = "halfjob_dashboard_sort";

export default function DashboardPage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [sortKey, setSortKey] = useState(() => localStorage.getItem(SORT_STORAGE_KEY) || "hardest");
  // Ref instead of state so triggering the celebration toast doesn't cause a
  // re-render loop and doesn't trip the set-state-in-effect lint rule.
  const toastedMilestonesRef = useRef(new Set());
  const navigate = useNavigate();

  const load = async () => {
    try {
      const r = await api.get("/dashboard");
      setData(r.data);
      setError(null);
      // Milestone celebration toast — fired from the event-driven load() call
      // rather than a useEffect so the strict set-state-in-effect rule passes.
      // queueMicrotask defers the toast/setState out of the React commit phase
      // (toast.success internally triggers setState in sonner).
      const ms = r.data?.hero?.pending_milestone;
      const hid = r.data?.hero?.id;
      if (ms && hid) {
        const key = `${hid}:${ms.key}`;
        if (!toastedMilestonesRef.current.has(key)) {
          toastedMilestonesRef.current.add(key);
          queueMicrotask(() => {
            toast.success(ms.headline, { description: ms.sub, duration: 6000 });
          });
          api.post(`/projects/${hid}/milestone-shown`, { key: ms.key }).catch(() => {});
        }
      }
    } catch (e) {
      setError({
        message: e?.response?.data?.detail || "Couldn't load your dashboard.",
        status: e?.response?.status || 0,
      });
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { localStorage.setItem(SORT_STORAGE_KEY, sortKey); }, [sortKey]);

  const sorted = useMemo(() => {
    if (!data) return null;
    return {
      ...data,
      ready_to_finish: sortProjects(data.ready_to_finish, sortKey),
      awaiting_materials: sortProjects(data.awaiting_materials, sortKey),
      in_progress: sortProjects(data.in_progress, sortKey),
      // Completed kept newest-first regardless of active sort
      completed: sortProjects(data.completed, "newest"),
    };
  }, [data, sortKey]);

  const currentSort = SORT_OPTIONS.find((o) => o.key === sortKey) || SORT_OPTIONS[0];

  return (
    <div className="min-h-screen pb-32" data-testid="dashboard-page">
      <header className="px-5 pt-6 pb-3 flex items-center justify-between max-w-md mx-auto w-full">
        <Logo />
        <button
          data-testid="new-project-fab-top"
          onClick={() => navigate("/projects/new")}
          className="bg-timber-900 text-masking rounded-xl px-4 py-2 font-bold text-sm flex items-center gap-1 btn-press shadow-tactile"
        >
          <Plus size={18} weight="bold" /> New
        </button>
      </header>

      <main className="px-5 max-w-md mx-auto w-full space-y-6">
        {error ? (
          <div
            data-testid="dashboard-error"
            className="tactile-card p-6 text-center border-2 border-block/40 bg-[#FBEDEE]"
          >
            <WarningCircle size={44} weight="duotone" className="mx-auto text-block" />
            <h2 className="font-heading text-xl font-bold mt-2 text-timber-900">
              {`Couldn't load your dashboard`}
            </h2>
            <p className="text-sm text-timber-700 mt-1">
              {error.status === 0
                ? "Looks like a connection hiccup. Check your internet and try again."
                : "Something's not right on our side. Give it another go in a moment."}
            </p>
            <Button
              data-testid="dashboard-retry-btn"
              onClick={() => { setLoading(true); load(); }}
              className="mt-4 bg-masking text-timber-900 hover:bg-masking-600 hover:text-white min-h-[48px] px-6 font-bold"
            >
              <ArrowClockwise size={18} weight="bold" className="mr-1" /> Try again
            </Button>
          </div>
        ) : loading ? (
          <DashboardLoading />
        ) : (
          <HeroCard project={sorted?.hero} onChange={load} />
        )}

        {!error && !loading && (
          <>
        <div className="flex items-center justify-between" data-testid="sort-bar">
          <p className="label-cap">Active projects</p>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button
                data-testid="sort-trigger"
                className="bg-chalk border-2 border-stone_border rounded-full px-3 py-1.5 text-xs font-bold text-timber-900 flex items-center gap-1.5 btn-press"
              >
                <SortAscending size={14} weight="bold" />
                {currentSort.label}
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="bg-chalk border-2 border-stone_border">
              {SORT_OPTIONS.map((o) => (
                <DropdownMenuItem
                  key={o.key}
                  data-testid={`sort-option-${o.key}`}
                  onClick={() => setSortKey(o.key)}
                  className={o.key === sortKey ? "font-bold text-masking" : ""}
                >
                  {o.label}
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <Section
          title="Ready to finish"
          subtitle="All materials on hand"
          color="ok"
          items={sorted?.ready_to_finish}
          testid="section-ready"
          emptyHint="Mark some materials as bought and they'll land here."
          accent="finish line"
        />
        <Section
          title="Awaiting materials"
          subtitle="Required items still to buy"
          color="masking"
          items={sorted?.awaiting_materials}
          testid="section-awaiting-materials"
          emptyHint="Nothing waiting on a shop run."
        />
        <Section
          title="In progress"
          subtitle="Started, more to plan"
          color="denim"
          items={sorted?.in_progress}
          testid="section-in-progress"
          emptyHint="No other active projects."
        />
        <Section
          title="Completed"
          subtitle="Half-jobs, finished"
          color="timber"
          items={sorted?.completed}
          testid="section-completed"
          collapsed
          emptyHint="Your first finish is coming."
        />
          </>
        )}
      </main>

      <button
        data-testid="new-project-fab"
        onClick={() => navigate("/projects/new")}
        className="fixed bottom-[100px] right-5 z-30 bg-masking text-timber-900 rounded-full w-16 h-16 flex items-center justify-center shadow-tactile-lg border-2 border-timber-900 btn-press"
        aria-label="New project"
      >
        <Plus size={28} weight="bold" />
      </button>
    </div>
  );
}

function Section({ title, subtitle, items, testid, emptyHint, accent, collapsed = false }) {
  const [open, setOpen] = useState(!collapsed);
  const list = items || [];

  return (
    <section data-testid={testid}>
      <button
        onClick={() => setOpen(!open)}
        className="w-full text-left flex items-end justify-between mb-2"
      >
        <div>
          <h2 className="font-heading text-xl font-bold text-timber-900">{title}</h2>
          <p className="text-xs text-timber-700">{subtitle}</p>
        </div>
        <span className="label-cap" data-testid={`${testid}-count`}>
          {list.length} {collapsed && (open ? "↑" : "↓")}
        </span>
      </button>
      {open && (
        list.length === 0 ? (
          <p className="text-sm text-timber-500 px-1">{emptyHint}</p>
        ) : (
          <div className="space-y-3">
            {list.map((p) => (
              <ProjectCard key={p.id} project={p} accent={accent} />
            ))}
          </div>
        )
      )}
    </section>
  );
}

// Distinct loading state for the hero card. Uses an animated shimmer + a
// "Loading your projects…" label so it never looks like an empty salmon
// rectangle (audit P0).
function DashboardLoading() {
  return (
    <div
      data-testid="dashboard-loading"
      className="bg-chalk border-2 border-stone_border rounded-2xl p-5 overflow-hidden"
      role="status"
      aria-live="polite"
    >
      <div className="flex items-center gap-2 text-timber-700">
        <span className="w-2 h-2 rounded-full bg-masking animate-bounce" style={{ animationDelay: "0ms" }} />
        <span className="w-2 h-2 rounded-full bg-masking animate-bounce" style={{ animationDelay: "120ms" }} />
        <span className="w-2 h-2 rounded-full bg-masking animate-bounce" style={{ animationDelay: "240ms" }} />
        <span className="text-xs font-bold uppercase tracking-wider text-timber-500 ml-1">
          Loading your projects…
        </span>
      </div>
      <div className="mt-3 space-y-2">
        <Skeleton className="h-6 w-2/3 rounded-lg" />
        <Skeleton className="h-4 w-1/2 rounded-lg" />
      </div>
      <div className="mt-4 bg-kraft border-2 border-dashed border-stone_border rounded-xl p-4 space-y-2">
        <Skeleton className="h-3 w-1/4 rounded-full" />
        <Skeleton className="h-5 w-3/4 rounded-lg" />
      </div>
      <div className="mt-4 grid grid-cols-2 gap-3">
        <Skeleton className="h-12 rounded-xl" />
        <Skeleton className="h-12 rounded-xl" />
      </div>
    </div>
  );
}
